import * as H from 'history';

import { HpiEligibilityFlow } from 'src/shared/models/shared';
import { useHistory } from 'react-router-dom';
import { useHpiEligibilityFlow } from '../hooks/useHpiEligibilityFlow';
import { InsurancePageName } from '../helpers/insuranceHelpers';
import { CoachNode } from 'src/generated/webapp_gql';
import { AnalyticsProperties } from '@modernhealth/web-analytics';

export enum ActionType {
    GO_HOME_OR_PROFILE = 'GO_HOME_OR_PROFILE',
    GO_REQUEST_PSYCH = 'GO_REQUEST_PSYCH',
    GO_EXTEND_THERAPY = 'GO_EXTEND_THERAPY',
    GO_INSURANCE_FORM = 'GO_INSURANCE_FORM',
    GO_RESOURCES = 'GO_RESOURCES',
    GO_MESSAGES = 'GO_MESSAGES',
}
interface ActionGeneratorProps {
    page: InsurancePageName;
    actionTitle: string;
    actionType: ActionType;
    location: H.Location<{
        session?: string | undefined;
        routedFromProfilePage?: boolean | undefined;
        hpiEligibilityFlow?: HpiEligibilityFlow | undefined;
    }>;
    myTherapists: CoachNode[];
    trackClick: (actionName: string, properties?: AnalyticsProperties | undefined) => void;
}

// myTherapists & analyticsClient must be passed as props otherwise they cause
// an ERROR "Rendered more hooks than during the previous render"
export function ActionGenerator({
    page,
    actionTitle,
    actionType,
    location,
    myTherapists,
    trackClick,
}: ActionGeneratorProps) {
    const history = useHistory();

    const session = location?.state?.session;
    const routedFromProfilePage = location?.state?.routedFromProfilePage ?? false;
    const hpiEligibilityFlow = useHpiEligibilityFlow();

    let page_name: string;
    switch (page) {
        case InsurancePageName.InsuranceInactive:
            page_name = 'Insurance Inactive Page';
            break;
        case InsurancePageName.InsuranceIneligible:
            page_name = 'Insurance Ineligible Page';
            break;
        case InsurancePageName.InsuranceError:
            page_name = 'Insurance Error Page';
            break;
        case InsurancePageName.InsuranceApproved:
        default:
            page_name = page;
            break;
    }

    let boilerplateAction = (specificAction: VoidFunction) => () => {
        trackClick(actionTitle, {
            page_name,
            hpiEligibilityFlow,
        });

        specificAction();
    };
    let actionMap: Map<ActionType, VoidFunction> = new Map([
        [
            ActionType.GO_HOME_OR_PROFILE,
            boilerplateAction(() => {
                history.push(routedFromProfilePage ? '/profile' : '/dashboard');
            }),
        ],
        [
            ActionType.GO_REQUEST_PSYCH,
            boilerplateAction(() => {
                history.push('/schedule-session/request-provider');
            }),
        ],
        [
            ActionType.GO_EXTEND_THERAPY,
            boilerplateAction(() => {
                if (session) {
                    history.push('/payments/session-review', {
                        session,
                    });
                    return;
                }

                switch (myTherapists.length) {
                    case 1:
                        history.push('/schedule-session/calendar', {
                            coachUuid: myTherapists[0].uuid,
                            isFirstSession: false,
                            disableBack: true,
                        });
                        break;
                    case 0:
                        history.push('/dashboard');
                        break;
                    default:
                        history.push('/schedule-session/select-coach');
                        break;
                }
            }),
        ],
        [
            ActionType.GO_INSURANCE_FORM,
            boilerplateAction(() => {
                history.push('/enter-insurance-plan-information', {
                    routedFromProfilePage,
                    hpiEligibilityFlow,
                    ...(session && { session }),
                });
            }),
        ],
        [
            ActionType.GO_RESOURCES,
            boilerplateAction(() => {
                history.push('/resources');
            }),
        ],
        [
            ActionType.GO_MESSAGES,
            boilerplateAction(() => {
                history.push('/messages');
            }),
        ],
    ]);

    if (!actionMap.has(actionType)) {
        throw Error(`actionType of ${actionType} does not exist in actionMap`);
    }

    return actionMap.get(actionType);
}
